import { useEffect, useState, useCallback } from 'react';
import { Card } from '@ftedn/ui';
import { useApi } from '../../hooks/useApi';

interface QueueItem {
  id: string;
  source: 'report' | 'auto-flag';
  contentRef?: string;
  authorId?: string;
  reporterId?: string;
  reason?: string;
  text?: string;
  score?: number;
  matchedWord?: string;
  status: string;
  createdAt: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#6b7280',
};

function getSeverity(score: number): string {
  if (score >= 0.8) return 'high';
  if (score >= 0.5) return 'medium';
  return 'low';
}

export function ModerationPage() {
  const { get, post } = useApi();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [tab, setTab] = useState<'queue' | 'log'>('queue');

  const loadQueue = useCallback(async () => {
    try {
      const data = await get('/moderation/queue');
      setItems(data.items || []);
    } catch (e) { setError(String(e)); }
  }, []);

  const loadActions = useCallback(async () => {
    try {
      const data = await get('/moderation/actions?limit=50');
      setActions(data.actions || []);
    } catch (e) { setError(String(e)); }
  }, []);

  useEffect(() => {
    if (tab === 'queue') loadQueue();
    else loadActions();
  }, [tab, loadQueue, loadActions]);

  const doAction = async (item: QueueItem, action: string, reason: string) => {
    setActionMsg(null);
    try {
      const body: any = { action, reason };
      if (item.contentRef) body.contentRef = item.contentRef;
      if (item.authorId) body.targetUserId = item.authorId;
      if (item.source === 'report') body.reportId = item.id;
      if (item.source === 'auto-flag') body.flagId = item.id;

      await post('/moderation/action', body);
      setActionMsg(`✅ ${action} applied`);
      loadQueue();
    } catch (e) { setActionMsg(`❌ ${String(e)}`); }
  };

  if (error) return <Card><p style={{ color: 'var(--color-error)' }}>Error: {error}</p></Card>;

  return (
    <div>
      <h1>🛡️ Content Moderation</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {(['queue', 'log'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
            fontWeight: tab === t ? 700 : 500, fontSize: '0.85rem',
            background: tab === t ? 'var(--color-primary, #18181b)' : 'var(--color-surface-2, #f3f4f6)',
            color: tab === t ? '#fff' : 'var(--color-text, #111)',
          }}>
            {t === 'queue' ? `📥 Queue (${items.length})` : '📋 Audit Log'}
          </button>
        ))}
      </div>

      {actionMsg && <Card><p>{actionMsg}</p></Card>}

      {tab === 'queue' ? (
        items.length === 0 ? (
          <Card><p style={{ color: 'var(--color-text-secondary)' }}>🎉 Queue is clear — nothing to review</p></Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {items.map(item => {
              const sev = getSeverity(item.score || 0.5);
              return (
                <Card key={item.id}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                        background: `${SEVERITY_COLORS[sev]}20`, color: SEVERITY_COLORS[sev],
                      }}>
                        {sev.toUpperCase()}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                        {item.source === 'auto-flag' ? '🤖 Auto-flagged' : '🚩 User report'}
                      </span>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                      {new Date(item.createdAt).toLocaleString()}
                    </span>
                  </div>

                  {item.text && (
                    <p style={{
                      background: 'var(--color-surface-2, #f9fafb)', padding: '8px 12px',
                      borderRadius: 8, fontSize: '0.9rem', margin: '0 0 0.5rem',
                      borderLeft: `3px solid ${SEVERITY_COLORS[sev]}`,
                    }}>
                      "{item.text.slice(0, 200)}{item.text.length > 200 ? '…' : ''}"
                    </p>
                  )}

                  <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '0 0 0.75rem' }}>
                    {item.contentRef && <>Content: <code>{item.contentRef}</code> • </>}
                    {item.reason && <>Reason: {item.reason} • </>}
                    {item.matchedWord && <>Match: "{item.matchedWord}" • </>}
                    Score: {((item.score || 0) * 100).toFixed(0)}%
                  </p>

                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button onClick={() => doAction(item, 'hide', 'Content hidden by moderator')} style={btnStyle('#f59e0b')}>👁️ Hide</button>
                    <button onClick={() => doAction(item, 'delete', 'Content deleted by moderator')} style={btnStyle('#ef4444')}>🗑️ Delete</button>
                    <button onClick={() => doAction(item, 'lock', 'Content locked by moderator')} style={btnStyle('#6366f1')}>🔒 Lock</button>
                    {item.authorId && (
                      <>
                        <button onClick={() => doAction(item, 'warn', 'Community guidelines warning')} style={btnStyle('#f97316')}>⚠️ Warn User</button>
                        <button onClick={() => doAction(item, 'suspend', 'Suspended for content violation')} style={btnStyle('#dc2626')}>🚫 Suspend</button>
                      </>
                    )}
                    <button onClick={() => doAction(item, 'restore', 'Content reviewed and approved')} style={btnStyle('#10b981')}>✅ Approve</button>
                  </div>
                </Card>
              );
            })}
          </div>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {actions.length === 0 ? (
            <Card><p style={{ color: 'var(--color-text-secondary)' }}>No moderation actions yet</p></Card>
          ) : actions.map((a: any) => (
            <Card key={a.id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span>
                  <strong>{a.action}</strong> by {a.actorId?.slice(0, 8)}…
                  {a.contentRef && <> on <code>{a.contentRef}</code></>}
                  {a.targetUserId && <> → user {a.targetUserId.slice(0, 8)}…</>}
                </span>
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {new Date(a.createdAt).toLocaleString()}
                </span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
                {a.reason}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function btnStyle(color: string): React.CSSProperties {
  return {
    padding: '6px 14px', borderRadius: 8, border: `1.5px solid ${color}`,
    background: 'transparent', color, fontWeight: 600, cursor: 'pointer',
    fontSize: '0.8rem', transition: 'all 0.2s',
  };
}
